import { useCallback, useState } from 'react';
import { FoldersService } from '../../services/foldersApiService';
import { useAppStore } from '../../store/store';

const foldersService = new FoldersService();

/**
 * Cache for file password checks to avoid repeated API calls
 */
const passwordCheckCache = new Map<number, { hasPassword: boolean; timestamp: number }>();
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to check file password status and manage password-related operations.
 */
export function useFilePassword() {
  const isFilePasswordSessionValid = useAppStore((s) => s.isFilePasswordSessionValid);
  const [checkingFolders, setCheckingFolders] = useState<Set<number>>(new Set());

  /**
   * Checks if a file (folder) has password protection.
   * Uses cache to avoid repeated API calls.
   * @param fileId - The file ID to check
   * @returns Promise resolving to true if file has password, false otherwise
   */
  const checkFolderPassword = useCallback(
    async (fileId: number): Promise<boolean> => {
      const cached = passwordCheckCache.get(fileId);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
        return cached.hasPassword;
      }

      if (checkingFolders.has(fileId)) {
        return cached?.hasPassword ?? false;
      }

      setCheckingFolders((prev) => new Set(prev).add(fileId));

      try {
        const response = await foldersService.checkFolderPassword(fileId);
        const hasPassword = response.passwordRequired && !response.hasValidAccess;

        passwordCheckCache.set(fileId, {
          hasPassword,
          timestamp: Date.now(),
        });

        return hasPassword;
      } catch (error) {
        console.error('Failed to check file password:', error);
        return false;
      } finally {
        setCheckingFolders((prev) => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      }
    },
    [checkingFolders],
  );

  /**
   * Checks if a file has password (synchronous, uses cache only).
   * @param fileId - The file ID to check
   * @returns True if cached data indicates password exists, false otherwise
   */
  const hasPassword = useCallback((fileId: number): boolean => {
    const cached = passwordCheckCache.get(fileId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
      return cached.hasPassword;
    }
    return false;
  }, []);

  /**
   * Checks if password is required for a file (considering session).
   * @param fileId - The file ID to check
   * @returns True if password is required, false if session is valid
   */
  const isPasswordRequired = useCallback(
    (fileId: number): boolean => {
      if (isFilePasswordSessionValid(fileId)) {
        return false;
      }
      return hasPassword(fileId);
    },
    [hasPassword, isFilePasswordSessionValid],
  );

  /**
   * Clears the password check cache for a specific file.
   * @param fileId - The file ID to clear cache for
   */
  const clearPasswordCache = useCallback((fileId: number) => {
    passwordCheckCache.delete(fileId);
  }, []);

  /**
   * Clears all password check cache.
   */
  const clearAllPasswordCache = useCallback(() => {
    passwordCheckCache.clear();
  }, []);

  return {
    checkFolderPassword,
    hasPassword,
    isPasswordRequired,
    clearPasswordCache,
    clearAllPasswordCache,
  };
}
